/**
 * CATALOG-SYNC-138-R1 — stored identity vs customer/manager display classification.
 * Pure helpers. Never coerce identifiers through Number (preserve leading zeroes).
 */

export type IdentifierDisplayMode =
  | "barcode_only"
  | "store_sku_only"
  | "store_sku_and_barcode"
  | "none";

export type IdentifierDisplayClassification = {
  has_store_sku: boolean;
  has_gtin: boolean;
  normalized_store_sku: string;
  normalized_gtin: string;
  store_sku_is_valid_gtin: boolean;
  gtin_is_valid: boolean;
  identifiers_equal: boolean;
  customer_display_mode: IdentifierDisplayMode;
  /** Presentation value for barcode row when mode is barcode_only. */
  display_barcode: string | null;
  /** Presentation value for store SKU row when mode includes store_sku. */
  display_store_sku: string | null;
};

const AS_HASH_RE = /^AS[0-9A-Z]{8}$/i;

/** Compare-normalization: trim + strip separators; keep leading zeroes; string only. */
export function normalizeIdentifierForCompare(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .replace(/[\s\u00A0\-–—]+/g, "")
    .replace(/[\uFF10-\uFF19]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30));
}

function cleanPresent(raw: string | null | undefined): string {
  const t = String(raw ?? "").trim();
  if (!t || t === "null" || t === "undefined" || t === "NaN") return "";
  if (AS_HASH_RE.test(t)) return "";
  return t;
}

/**
 * Valid GTIN family for display: 8/12/13/14 digits + GS1 checksum.
 * Invalid digit strings are NOT treated as barcodes (Rule H).
 */
export function isValidGtinString(raw: string | null | undefined): boolean {
  const n = normalizeIdentifierForCompare(raw);
  if (!/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(n)) return false;
  const payload = n.slice(0, -1);
  const check = Number(n[n.length - 1]);
  let sum = 0;
  for (let i = 0; i < payload.length; i += 1) {
    const digit = Number(payload[payload.length - 1 - i]);
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === check;
}

export function classifyProductIdentifierDisplay(input: {
  storeSku?: string | null | undefined;
  gtin?: string | null | undefined;
}): IdentifierDisplayClassification {
  const storeRaw = cleanPresent(input.storeSku);
  const gtinRaw = cleanPresent(input.gtin);
  const normalized_store_sku = storeRaw ? normalizeIdentifierForCompare(storeRaw) : "";
  const normalized_gtin = gtinRaw ? normalizeIdentifierForCompare(gtinRaw) : "";
  const store_sku_is_valid_gtin = Boolean(storeRaw) && isValidGtinString(storeRaw);
  const gtin_is_valid = Boolean(gtinRaw) && isValidGtinString(gtinRaw);
  const has_store_sku = Boolean(storeRaw);
  const has_gtin = Boolean(gtinRaw) && gtin_is_valid;
  const identifiers_equal =
    Boolean(normalized_store_sku) &&
    Boolean(normalized_gtin) &&
    normalized_store_sku === normalized_gtin;

  // Rule A: equal after normalize → barcode_only
  if (has_store_sku && has_gtin && identifiers_equal) {
    return {
      has_store_sku,
      has_gtin,
      normalized_store_sku,
      normalized_gtin,
      store_sku_is_valid_gtin,
      gtin_is_valid,
      identifiers_equal: true,
      customer_display_mode: "barcode_only",
      display_barcode: gtinRaw || storeRaw,
      display_store_sku: null,
    };
  }

  // Rule B: store_sku is valid GTIN and gtin missing → barcode_only (presentation only)
  if (has_store_sku && store_sku_is_valid_gtin && !has_gtin) {
    return {
      has_store_sku,
      has_gtin: false,
      normalized_store_sku,
      normalized_gtin: "",
      store_sku_is_valid_gtin: true,
      gtin_is_valid: false,
      identifiers_equal: false,
      customer_display_mode: "barcode_only",
      display_barcode: storeRaw,
      display_store_sku: null,
    };
  }

  // Rule D: both exist and differ
  if (has_store_sku && has_gtin && !identifiers_equal) {
    // If store_sku itself is ONLY a valid GTIN equal... already handled.
    // If store is non-GTIN or different GTIN-shaped string that failed equality:
    if (!store_sku_is_valid_gtin) {
      return {
        has_store_sku,
        has_gtin,
        normalized_store_sku,
        normalized_gtin,
        store_sku_is_valid_gtin,
        gtin_is_valid,
        identifiers_equal: false,
        customer_display_mode: "store_sku_and_barcode",
        display_barcode: gtinRaw,
        display_store_sku: storeRaw,
      };
    }
    // store is valid GTIN but different from gtin → show both (distinct)
    return {
      has_store_sku,
      has_gtin,
      normalized_store_sku,
      normalized_gtin,
      store_sku_is_valid_gtin,
      gtin_is_valid,
      identifiers_equal: false,
      customer_display_mode: "store_sku_and_barcode",
      display_barcode: gtinRaw,
      display_store_sku: storeRaw,
    };
  }

  // Rule C: non-GTIN store_sku only (or store with invalid gtin companion ignored)
  if (has_store_sku && !store_sku_is_valid_gtin) {
    return {
      has_store_sku,
      has_gtin,
      normalized_store_sku,
      normalized_gtin,
      store_sku_is_valid_gtin: false,
      gtin_is_valid,
      identifiers_equal: false,
      customer_display_mode: has_gtin ? "store_sku_and_barcode" : "store_sku_only",
      display_barcode: has_gtin ? gtinRaw : null,
      display_store_sku: storeRaw,
    };
  }

  // Barcode only (valid gtin, no usable store sku)
  if (has_gtin) {
    return {
      has_store_sku: false,
      has_gtin: true,
      normalized_store_sku: "",
      normalized_gtin,
      store_sku_is_valid_gtin: false,
      gtin_is_valid: true,
      identifiers_equal: false,
      customer_display_mode: "barcode_only",
      display_barcode: gtinRaw,
      display_store_sku: null,
    };
  }

  // Rule E
  return {
    has_store_sku: false,
    has_gtin: false,
    normalized_store_sku: "",
    normalized_gtin: "",
    store_sku_is_valid_gtin: false,
    gtin_is_valid: false,
    identifiers_equal: false,
    customer_display_mode: "none",
    display_barcode: null,
    display_store_sku: null,
  };
}

export type ManagerIdentifierRowKind = "store_sku" | "barcode" | "prom_id" | "compact_barcode_sku" | "missing_store_sku";

export function resolveManagerIdentifierRows(
  lang: "ua" | "en",
  input: {
    storeSku?: string | null | undefined;
    gtin?: string | null | undefined;
    promProductId?: string | null | undefined;
  },
): Array<{ kind: ManagerIdentifierRowKind; label: string; value: string }> {
  const c = classifyProductIdentifierDisplay({ storeSku: input.storeSku, gtin: input.gtin });
  const rows: Array<{ kind: ManagerIdentifierRowKind; label: string; value: string }> = [];
  const prom = cleanPresent(input.promProductId);

  // Equal / store-SKU-is-GTIN → one compact operational row (stored fields remain separate).
  if (
    c.customer_display_mode === "barcode_only" &&
    c.has_store_sku &&
    c.display_barcode
  ) {
    rows.push({
      kind: "compact_barcode_sku",
      label: lang === "en" ? "Barcode / Prom SKU" : "Штрихкод / SKU Prom",
      value: c.display_barcode,
    });
  } else if (c.customer_display_mode === "store_sku_and_barcode") {
    if (c.display_store_sku) {
      rows.push({
        kind: "store_sku",
        label: lang === "en" ? "Store SKU" : "Артикул магазину",
        value: c.display_store_sku,
      });
    }
    if (c.display_barcode) {
      rows.push({
        kind: "barcode",
        label: lang === "en" ? "Barcode" : "Штрихкод",
        value: c.display_barcode,
      });
    }
  } else if (c.customer_display_mode === "store_sku_only" && c.display_store_sku) {
    rows.push({
      kind: "store_sku",
      label: lang === "en" ? "Store SKU" : "Артикул магазину",
      value: c.display_store_sku,
    });
  } else if (!c.has_store_sku) {
    rows.push({
      kind: "missing_store_sku",
      label: lang === "en" ? "Store SKU" : "Артикул магазину",
      value: lang === "en" ? "Not set" : "Артикул не задано",
    });
    if (c.display_barcode) {
      rows.push({
        kind: "barcode",
        label: lang === "en" ? "Barcode" : "Штрихкод",
        value: c.display_barcode,
      });
    }
  }

  if (prom) {
    rows.push({
      kind: "prom_id",
      label: lang === "en" ? "Prom ID" : "ID Prom",
      value: prom,
    });
  }
  return rows;
}
